import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import _isEmpty from 'lodash/isEmpty';
import { Button, Drawer, Modal } from '@duxton-web/ui';

import {
  OTHER_ITEM,
  PAYMENT_ITEM_TYPE_LOCALES,
  PAYMENT_ITEM_DESCRIPTION_LOCALES,
  SERVICE_FEE,
} from '../../../constants/payment';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';
import { absAmount, formatPrice } from '../../../common/utils/currency';

import styles from './pod-sub-items-modal.less';

const getTitle = feeType => {
  // override for service fee
  if (feeType === SERVICE_FEE) {
    return I18n.t('paymentDetails.subItemsModal.serviceFees');
  }
  return I18n.t(
    PAYMENT_ITEM_TYPE_LOCALES[feeType] || PAYMENT_ITEM_TYPE_LOCALES[OTHER_ITEM],
  );
};

const PayOnDeliverySubItemsModal = ({
  isOpen,
  feeType,
  subItems,
  symbol,
  onClose,
}) => {
  const isMobile = useCheckMobileScreen();

  const title = getTitle(feeType);
  const content = useMemo(
    () => (
      <div
        className={styles.podSubItemsContent}
        data-testid="pod-sub-items-content"
      >
        {!_isEmpty(subItems) &&
          subItems.map(item => {
            const itemDescriptionLocale =
              PAYMENT_ITEM_DESCRIPTION_LOCALES[item.type];

            return (
              <div
                className={styles.podSubItem}
                key={`${item.type}`}
                data-testid={`pod-sub-item-${item.type}`}
              >
                <div className={styles.podSubItemLabelLine}>
                  <span
                    className={styles.podSubItemLabel}
                    data-testid="pod-sub-item-label"
                  >
                    {I18n.t(
                      PAYMENT_ITEM_TYPE_LOCALES[item.type] ||
                        PAYMENT_ITEM_TYPE_LOCALES[OTHER_ITEM],
                    )}
                  </span>
                  <span
                    className={styles.podSubItemAmount}
                    data-testid="pod-sub-item-amount"
                  >
                    {formatPrice({ amount: absAmount(item.amount), symbol })}
                  </span>
                </div>
                {itemDescriptionLocale && (
                  <div data-testid="pod-sub-item-description">
                    {I18n.t(itemDescriptionLocale)}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    ),
    [subItems],
  );

  const button = useMemo(
    () => (
      <Button
        block
        size="large"
        type="primary"
        onClick={onClose}
        className={styles.closeButton}
        data-testid="pod-sub-items-close-button"
      >
        {I18n.t('paymentDetails.subItemsModal.button')}
      </Button>
    ),
    [onClose],
  );

  return isMobile ? (
    <Drawer
      open={isOpen}
      placement="bottom"
      height="auto"
      closable={false}
      onClose={onClose}
      className={styles.podSubItemsDrawer}
      data-testid="pod-sub-items-drawer"
    >
      <h4
        className={styles.podSubItemDrawerHeader}
        data-testid="pod-sub-items-drawer-header"
      >
        {title}
      </h4>
      {content}
      {button}
    </Drawer>
  ) : (
    <Modal
      open={isOpen}
      title={title}
      closable={false}
      className={styles.podSubItemsModal}
      footer={button}
      data-testid="pod-sub-items-modal"
    >
      {content}
    </Modal>
  );
};

PayOnDeliverySubItemsModal.propTypes = {
  isOpen: PropTypes.bool,
  feeType: PropTypes.string,
  subItems: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      amount: PropTypes.string,
      paymentMethod: PropTypes.string,
    }),
  ),
  symbol: PropTypes.string,
  onClose: PropTypes.func,
};

PayOnDeliverySubItemsModal.defaultProps = {
  isOpen: false,
  feeType: '',
  subItems: null,
  symbol: '',
  onClose: () => {},
};

export default PayOnDeliverySubItemsModal;
